/**
 * Ministries and roles (read-only): ministry memberships, department memberships and designations
 * from DepartmentManagement. Changes are made in the Departments module.
 *
 *  - admin: api/ministries/member/{id}/view, api/member-in-departments/member/{id}/view and
 *           api/memberdesignations/member/{id}/view (each gated by its own right)
 *  - self:  api/me/ministries, api/me/departments and api/me/designations (any signed-in user,
 *           only their own rows)
 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconAlertCircle, IconHierarchy, IconId, IconCrown, IconStar, IconUsersGroup } from '@tabler/icons-react';
import { Card, Stack, Group, Text, Alert, Skeleton, Badge, SimpleGrid, ThemeIcon } from '@/components/ui';
import {
  useMemberDesignationService,
  useMemberInDepartmentService,
  useMinistryService,
  useMyMinistriesService,
} from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { errorMessage, formatStamp } from '../memberFormat';

function tint(color?: string | null) {
  return color ? { backgroundColor: `${color}22`, color } : undefined;
}

interface SectionProps {
  icon: ReactNode;
  color: string;
  title: string;
  allowed: boolean;
  lockedText: string;
  loading: boolean;
  error: unknown;
  empty: boolean;
  emptyText: string;
  children: ReactNode;
}

function Section({ icon, color, title, allowed, lockedText, loading, error, empty, emptyText, children }: SectionProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group gap="sm" mb="sm">
        <ThemeIcon variant="light" color={color} size="lg">{icon}</ThemeIcon>
        <Text fw={600}>{title}</Text>
      </Group>
      {!allowed && <Text size="sm" c="dimmed">{lockedText}</Text>}
      {allowed && loading && <Stack gap="xs"><Skeleton height={40} /><Skeleton height={40} /></Stack>}
      {allowed && !loading && !!error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(error)}</Alert>}
      {allowed && !loading && !error && empty && <Text size="sm" c="dimmed">{emptyText}</Text>}
      {allowed && !loading && !error && !empty && <Stack gap="xs">{children}</Stack>}
    </Card>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return <Badge size="sm" variant="light" color={active ? 'green' : 'gray'}>{active ? 'Active' : 'Inactive'}</Badge>;
}

export function MinistriesTab({ api, member, perms }: MemberTabProps) {
  const self = api.mode === 'self';
  const ministryService = useMinistryService();
  const departmentsService = useMemberInDepartmentService();
  const designationsService = useMemberDesignationService();
  const myService = useMyMinistriesService();

  const ministries = useQuery({
    queryKey: [...api.scope, 'ministries'],
    queryFn: () => (self ? myService.getMyMinistries() : ministryService.getMinistryViewsByMember(member.memberID)),
    enabled: perms.ministries,
  });
  const departments = useQuery({
    queryKey: [...api.scope, 'departments'],
    queryFn: () => (self ? myService.getMyDepartments() : departmentsService.getViewsByMember(member.memberID)),
    enabled: perms.departments,
  });
  const designations = useQuery({
    queryKey: [...api.scope, 'designations'],
    queryFn: () => (self ? myService.getMyDesignations() : designationsService.getMemberDesignationViewsByMember(member.memberID)),
    enabled: perms.designations,
  });

  const ministryRows = [...(ministries.data ?? [])].sort((a, b) => Number(b.isActive) - Number(a.isActive) || (a.ministryName ?? '').localeCompare(b.ministryName ?? ''));
  const deptRows = [...(departments.data ?? [])].sort((a, b) => Number(b.isActive) - Number(a.isActive) || (a.departmentName ?? '').localeCompare(b.departmentName ?? ''));
  const desigRows = [...(designations.data ?? [])].sort((a, b) => Number(b.isMain) - Number(a.isMain) || (a.designationName ?? '').localeCompare(b.designationName ?? ''));

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Text fw={600}>Ministries and roles</Text>
        <Text size="sm" c="dimmed">
          {self
            ? 'The ministries and departments you serve in and the roles you hold. To change them, speak to your ministry or department leader.'
            : 'Ministries and departments this person serves in and the roles they hold. Manage these in the Departments module.'}
        </Text>
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
        <Section
          icon={<IconUsersGroup size={18} />}
          color="teal"
          title="Ministries"
          allowed={perms.ministries}
          lockedText="You need the Ministry or Member.Department right to see ministry memberships."
          loading={ministries.isLoading}
          error={ministries.error}
          empty={ministryRows.length === 0}
          emptyText={self ? 'You are not in a ministry yet.' : 'Not in any ministry.'}
        >
          {ministryRows.map((m) => (
            <Group key={m.ministryID} justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={6}>
                  <Badge variant="light" color="teal">{m.ministryName ?? `Ministry ${m.ministryID}`}</Badge>
                  {m.roleName && <Badge size="xs" variant="outline" color="gray">{m.roleName}</Badge>}
                </Group>
                {m.createdDateTime ? <Text size="xs" c="dimmed">Since {formatStamp(m.createdDateTime)}</Text> : null}
              </Stack>
              <StatusBadge active={m.isActive} />
            </Group>
          ))}
        </Section>

        <Section
          icon={<IconHierarchy size={18} />}
          color="indigo"
          title="Departments"
          allowed={perms.departments}
          lockedText="You need the Member.Department right to see department memberships."
          loading={departments.isLoading}
          error={departments.error}
          empty={deptRows.length === 0}
          emptyText={self ? 'You are not in a department.' : 'Not in any department.'}
        >
          {deptRows.map((d) => (
            <Group key={d.departmentID} justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={6}>
                  <Badge variant="light" style={tint(d.departmentColor)}>{d.departmentName ?? `Department ${d.departmentID}`}</Badge>
                  {d.isHead && <Badge size="xs" color="yellow" variant="light" leftSection={<IconCrown size={10} />}>Head</Badge>}
                </Group>
                <Text size="xs" c="dimmed">Since {formatStamp(d.createdDateTime)}{d.tenureLevel ? ` · ${d.tenureLevel}` : ''}</Text>
              </Stack>
              <StatusBadge active={d.isActive} />
            </Group>
          ))}
        </Section>

        <Section
          icon={<IconId size={18} />}
          color="grape"
          title="Roles and designations"
          allowed={perms.designations}
          lockedText="You need the Member.Designation right to see designations."
          loading={designations.isLoading}
          error={designations.error}
          empty={desigRows.length === 0}
          emptyText={self ? 'You have no designations.' : 'No designations.'}
        >
          {desigRows.map((d) => (
            <Group key={d.designationID} justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={6}>
                  <Badge variant="light" style={tint(d.designationColor)}>{d.designationName ?? `Designation ${d.designationID}`}</Badge>
                  {d.isMain && <Badge size="xs" color="orange" variant="light" leftSection={<IconStar size={10} />}>Main</Badge>}
                </Group>
                <Text size="xs" c="dimmed">Since {formatStamp(d.createdDateTime)}{d.designationType ? ` · ${d.designationType}` : ''}</Text>
              </Stack>
              <StatusBadge active={d.isActive} />
            </Group>
          ))}
        </Section>
      </SimpleGrid>
    </Stack>
  );
}
